<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="cancelModal"></div>

    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 rounded-xl shadow-2xl text-white overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 id="modal-title" class="text-xl font-semibold text-indigo-300">สร้างแผนที่โลกวิญญาณ</h2>
        <button @click="cancelModal" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700 bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          class="flex-1 px-4 py-3 text-sm font-medium text-white transition duration-200"
          :class="{
            'bg-indigo-600 border-b-2 border-indigo-500': currentTab === tab.id,
            'bg-gray-600 hover:bg-gray-500': currentTab !== tab.id
          }"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="h-96 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
        <form @submit.prevent="handleAddMap">
          <!-- Basic Info Tab -->
          <div v-if="currentTab === 'basic'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">ข้อมูลพื้นฐาน</h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ประเภทแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="type"
                  type="text"
                  readonly
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 text-white cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="name"
                  type="text"
                  placeholder="เช่น สุสานร้าง"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลิงก์รูปภาพ <span class="text-red-500">*</span></label>
                <input
                  v-model="imageUrl"
                  type="url"
                  placeholder="https://example.com/map.jpg"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <div v-if="imageUrl" class="mt-2">
                  <img :src="imageUrl" alt="Map Preview" class="w-24 h-24 object-cover rounded-lg" @error="imageUrl = ''" />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เลเวลที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredLevel"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="roomRequired"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredEnergy"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลดความสุข <span class="text-red-500">*</span></label>
                <input
                  v-model.number="reduceHappy"
                  type="number"
                  min="0"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อเสียงที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredRenown"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนที่สามารถหนีโดยใช้เหรียญได้ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="escapeAttempts"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเหรียญที่ใช้สำหรับหนี <span class="text-red-500">*</span></label>
                <input
                  v-model.number="escapeCoins"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสพบเจอผู้เล่นคนอื่น (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="meetPlayerChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ค่าเข้า</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เหรียญ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredCoins"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">ทอง <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredGold"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เพชร <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredDiamonds"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">แคช <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredCash"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">แผนที่ที่ต้องมีก่อนหน้า</label>
                <input
                  v-model="requiredMap"
                  type="text"
                  placeholder="เช่น สุสานร้าง (เว้นว่างได้)"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ตึกที่อนุญาตให้สำรวจ <span class="text-gray-500 text-xs font-normal">(ไม่เลือก = ทุกตึก)</span></label>
                <div class="flex items-center gap-4 mt-2">
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="1"
                      v-model="allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 1</span>
                  </label>
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="2"
                      v-model="allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 2</span>
                  </label>
                </div>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ทักษะที่ต้องมี</h4>
              <div class="space-y-3">
                <div
                  v-for="(skill, index) in requiredSkills"
                  :key="index"
                  class="grid grid-cols-12 items-center gap-3 animate-fade-in"
                >
                  <div class="col-span-10">
                    <input
                      v-model="skill.name"
                      type="text"
                      placeholder="ชื่อทักษะ (เช่น Fire Magic)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeRequiredSkill(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบทักษะ"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
                <button
                  @click="addRequiredSkill"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มทักษะ
                </button>
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ไอเทมที่ต้องใช้ในการสำรวจ</h4>
              <div class="space-y-3">
                <div
                  v-for="(item, index) in requiredItems"
                  :key="index"
                  class="grid grid-cols-12 items-center gap-3 animate-fade-in"
                >
                  <div class="col-span-10">
                    <input
                      v-model="item.name"
                      type="text"
                      placeholder="ชื่อไอเทม (เช่น ไฟฉาย)"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    />
                  </div>
                  <div class="col-span-2">
                    <button
                      v-if="index > 0"
                      @click="removeRequiredItem(index)"
                      type="button"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบไอเทม"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
                <button
                  @click="addRequiredItem"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มไอเทม
                </button>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">สถานะแผนที่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isActive ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleActive"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isActive ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะแผนที่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isActive ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอพ่อค้าเร่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isMerchantEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleMerchantEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isMerchantEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะพ่อค้าเร่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isMerchantEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Ghosts Tab -->
          <div v-if="currentTab === 'ghosts'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">วิญญาณในแผนที่</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนวิญญาณขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="minGhosts"
                  type="number"
                  min="1"
                  placeholder="5"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนวิญญาณสูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="maxGhosts"
                  type="number"
                  :min="minGhosts"
                  placeholder="10"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="maxGhosts < minGhosts" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
            </div>
            <div class="space-y-3">
              <div
                v-for="(ghost, index) in ghosts"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="ghost.name"
                    type="text"
                    placeholder="ชื่อวิญญาณ (เช่น Ghost Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="ghost.chance"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeGhost(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบวิญญาณ"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addGhost"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มวิญญาณ
                </button>
                <button
                  @click="normalizeGhostChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  คำนวณโอกาส
                </button>
              </div>
              <div v-if="totalGhostChance > 0" class="text-sm text-gray-400 mt-2">
                <p>โอกาสการเจอวิญญาณ (รวม: {{ totalGhostChance.toFixed(2) }}%):</p>
                <p v-if="remainingGhostChance > 0" class="text-yellow-500">เหลือ: {{ remainingGhostChance.toFixed(2) }}%</p>
                <p v-else-if="remainingGhostChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingGhostChance).toFixed(2) }}%</p>
                <ul class="list-disc list-inside">
                  <li v-for="(ghost, index) in ghosts" :key="index">
                    {{ ghost.name || 'ยังไม่ระบุ' }}: {{ ghost.chance || 0 }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Boss Ghosts Tab -->
          <div v-if="currentTab === 'bossGhosts'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">บอสวิญญาณในแผนที่</h3>
            <div class="space-y-3">
              <div
                v-for="(bossGhost, index) in bossGhosts"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="bossGhost.name"
                    type="text"
                    placeholder="ชื่อบอสวิญญาณ (เช่น Boss Ghost King)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="bossGhost.chance"
                    type="number"
                    min="1"
                    max="10000"
                    placeholder="1-10000 (10000 = 100%)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeBossGhost(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบบอสวิญญาณ"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addBossGhost"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มบอสวิญญาณ
                </button>
                <button
                  @click="normalizeBossGhostChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  คำนวณโอกาส
                </button>
              </div>
              <div v-if="totalBossGhostChance > 0" class="text-sm text-gray-400 mt-2">
                <p>โอกาสการเจอบอสวิญญาณ (รวม: {{ (totalBossGhostChance / 100).toFixed(2) }}%):</p>
                <p v-if="remainingBossGhostChance > 0" class="text-yellow-500">เหลือ: {{ (remainingBossGhostChance / 100).toFixed(2) }}%</p>
                <p v-else-if="remainingBossGhostChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingBossGhostChance / 100).toFixed(2) }}%</p>
                <ul class="list-disc list-inside">
                  <li v-for="(bossGhost, index) in bossGhosts" :key="index">
                    {{ bossGhost.name || 'ยังไม่ระบุ' }}: {{ (bossGhost.chance / 100).toFixed(2) || 0 }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Merchants Tab -->
          <div v-if="currentTab === 'merchants'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">พ่อค้าเร่ในแผนที่</h3>
            <div v-if="!isMerchantEnabled" class="text-red-500 mb-4">พ่อค้าเร่ถูกปิดใช้งาน</div>
            <div v-else class="space-y-3">
              <div
                v-for="(merchant, index) in merchants"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="merchant.name"
                    type="text"
                    placeholder="ชื่อพ่อค้าเร่ (เช่น Merchant Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="merchant.chance"
                    type="number"
                    min="1"
                    max="1000"
                    placeholder="1-1000 (1000 = 10%)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeMerchant(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบพ่อค้าเร่"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addMerchant"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มพ่อค้าเร่
                </button>
              </div>
            </div>
          </div>

          <!-- Rewards Tab -->
          <div v-if="currentTab === 'rewards'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">รางวัลเมื่อสำเร็จ</h3>
            <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลเพชร</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropMin"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรสูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropMax"
                  type="number"
                  :min="diamondDropMin"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="diamondDropMax < diamondDropMin" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสได้รับเพชร (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
            <div class="space-y-3">
              <div
                v-for="(reward, index) in rewards"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="reward.itemName"
                    type="text"
                    placeholder="ชื่ออุปกรณ์ (เช่น กระสุน)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="reward.dropChance"
                    type="number"
                    min="0"
                    max="10000"
                    placeholder="จาก 10,000"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    @click="removeReward(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบรางวัล"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <button
                @click="addReward"
                type="button"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
              >
                + เพิ่มรางวัล
              </button>
            </div>
          </div>

          <!-- Situations Tab -->
          <div v-if="currentTab === 'situations'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">สถานการณ์ในแผนที่</h3>
            <div class="space-y-3">
              <div
                v-for="(situation, situationIndex) in situations"
                :key="situationIndex"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div class="space-y-4">
                  <div class="grid grid-cols-1 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อสถานการณ์ <span class="text-red-500">*</span></label>
                      <input
                        v-model="situation.name"
                        type="text"
                        placeholder="เช่น การปรากฏตัวของวิญญาณ"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนวิญญาณขั้นต่ำ <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.minGhosts"
                          type="number"
                          min="1"
                          placeholder="5"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนวิญญาณสูงสุด <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.maxGhosts"
                          type="number"
                          :min="situation.minGhosts"
                          placeholder="10"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                        <p v-if="situation.maxGhosts < situation.minGhosts" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
                      </div>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสเกิด (%) <span class="text-red-500">*</span></label>
                      <input
                        v-model.number="situation.chance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="10"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <div class="mt-4">
                    <h4 class="text-md font-medium text-gray-300 mb-2">รางวัลของสถานการณ์</h4>
                    <div class="space-y-3">
                      <div
                        v-for="(reward, rewardIndex) in situation.rewards"
                        :key="rewardIndex"
                        class="p-3 bg-gray-800 bg-opacity-50 rounded-lg"
                      >
                        <div class="grid grid-cols-12 items-center gap-3">
                          <div class="col-span-6 sm:col-span-7">
                            <input
                              v-model="reward.itemName"
                              type="text"
                              placeholder="ชื่อรางวัล (เช่น กระสุน)"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-4 sm:col-span-3">
                            <input
                              v-model.number="reward.dropChance"
                              type="number"
                              min="0"
                              max="10000"
                              placeholder="จาก 10,000"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-2">
                            <button
                              @click="removeSituationReward(situationIndex, rewardIndex)"
                              type="button"
                              class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                              aria-label="ลบรางวัล"
                            >
                              ลบ
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        @click="addSituationReward(situationIndex)"
                        type="button"
                        class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                      >
                        + เพิ่มรางวัล
                      </button>
                    </div>
                  </div>
                  <div class="flex justify-end mt-3">
                    <button
                      v-if="situationIndex > 0"
                      @click="removeSituation(situationIndex)"
                      type="button"
                      class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบสถานการณ์"
                    >
                      ลบสถานการณ์
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addSituation"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มสถานการณ์
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Fixed Action Buttons -->
      <div class="flex justify-end gap-3 p-6 border-t border-gray-700 bg-gray-800">
        <button
          @click="cancelModal"
          type="button"
          class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-200"
        >
          ยกเลิก
        </button>
        <button
          type="submit"
          @click="handleAddMap"
          class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
          :disabled="totalGhostChance > 100 || maxGhosts < minGhosts || diamondDropMax < diamondDropMin || hasInvalidSituations || totalBossGhostChance > 10000"
        >
          สร้างแผนที่
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, computed } from 'vue';

export default {
  setup(props, context) {
    const cancelModal = () => {
      context.emit('cancelModal');
    };

    const type = ref('โลกวิญญาณ');
    const name = ref('');
    const imageUrl = ref('');
    const requiredLevel = ref(1);
    const roomRequired = ref(1);
    const requiredEnergy = ref(0);
    const reduceHappy = ref(1);
    const requiredCoins = ref(0);
    const requiredGold = ref(0);
    const requiredDiamonds = ref(0);
    const requiredCash = ref(0);
    const requiredMap = ref('');
    const allowedBuildings = ref([]);
    const requiredRenown = ref(0);
    const escapeAttempts = ref(0);
    const escapeCoins = ref(0);
    const isActive = ref(true);
    const isMerchantEnabled = ref(true);
    const minGhosts = ref(5);
    const maxGhosts = ref(10);
    const ghosts = ref([{ name: '', chance: 0 }]);
    const bossGhosts = ref([{ name: '', chance: 0 }]);
    const merchants = ref([{ name: '', chance: 0 }]);
    const rewards = ref([{ itemName: '', dropChance: 0 }]);
    const situations = ref([{ name: '', minGhosts: 1, maxGhosts: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] }]);
    const diamondDropMin = ref(0);
    const diamondDropMax = ref(0);
    const diamondDropChance = ref(0);
    const requiredSkills = ref([{ name: '' }]);
    const meetPlayerChance = ref(0);
    const requiredItems = ref([{ name: '' }]);

    // Tab Management
    const tabs = [
      { id: 'basic', name: 'ข้อมูลพื้นฐาน' },
      { id: 'ghosts', name: 'วิญญาณในแผนที่' },
      { id: 'bossGhosts', name: 'บอสวิญญาณ' },
      { id: 'merchants', name: 'พ่อค้าเร่' },
      { id: 'rewards', name: 'รางวัล' },
      { id: 'situations', name: 'สถานการณ์' }
    ];
    const currentTab = ref('basic');

    const switchTab = (tabId) => {
      currentTab.value = tabId;
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const totalGhostChance = computed(() => {
      return ghosts.value.reduce((sum, ghost) => sum + (ghost.chance || 0), 0);
    });

    const remainingGhostChance = computed(() => 100 - totalGhostChance.value);

    const totalBossGhostChance = computed(() => {
      return bossGhosts.value.reduce((sum, bossGhost) => sum + (bossGhost.chance || 0), 0);
    });

    const remainingBossGhostChance = computed(() => 10000 - totalBossGhostChance.value);

    const hasInvalidSituations = computed(() => {
      return situations.value.some(s => s.maxGhosts < s.minGhosts);
    });

    const normalizeGhostChances = () => {
      const currentTotal = totalGhostChance.value;

      if (currentTotal === 0) {
        const baseChance = Math.floor(100 / ghosts.value.length);
        let remaining = 100 - baseChance * ghosts.value.length;

        ghosts.value.forEach((ghost, index) => {
          ghost.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 100 / currentTotal;
        const scaledChances = ghosts.value.map(ghost => (ghost.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map(chance => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 100 - totalFloored;

        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);

        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }

        ghosts.value.forEach((ghost, index) => {
          ghost.chance = flooredChances[index];
        });
      }
    };

    const addGhost = () => {
      ghosts.value.push({ name: '', chance: 0 });
    };

    const removeGhost = (index) => {
      if (ghosts.value.length > 1) {
        ghosts.value.splice(index, 1);
      }
    };

    const normalizeBossGhostChances = () => {
      const currentTotal = totalBossGhostChance.value;

      if (currentTotal === 0) {
        const baseChance = Math.floor(10000 / bossGhosts.value.length);
        let remaining = 10000 - baseChance * bossGhosts.value.length;

        bossGhosts.value.forEach((bossGhost, index) => {
          bossGhost.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 10000 / currentTotal;
        const scaledChances = bossGhosts.value.map(bossGhost => (bossGhost.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map(chance => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 10000 - totalFloored;

        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);

        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }

        bossGhosts.value.forEach((bossGhost, index) => {
          bossGhost.chance = flooredChances[index];
        });
      }
    };

    const addBossGhost = () => {
      bossGhosts.value.push({ name: '', chance: 0 });
    };

    const removeBossGhost = (index) => {
      if (bossGhosts.value.length > 1) {
        bossGhosts.value.splice(index, 1);
      }
    };

    const addMerchant = () => {
      merchants.value.push({ name: '', chance: 0 });
    };

    const removeMerchant = (index) => {
      if (merchants.value.length > 1) {
        merchants.value.splice(index, 1);
      }
    };

    const addReward = () => {
      rewards.value.push({ itemName: '', dropChance: 0 });
    };

    const removeReward = (index) => {
      if (rewards.value.length > 1) {
        rewards.value.splice(index, 1);
      }
    };

    const addSituation = () => {
      situations.value.push({ name: '', minGhosts: 1, maxGhosts: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] });
    };

    const removeSituation = (index) => {
      if (situations.value.length > 1) {
        situations.value.splice(index, 1);
      }
    };

    const addSituationReward = (situationIndex) => {
      situations.value[situationIndex].rewards.push({ itemName: '', dropChance: 0 });
    };

    const removeSituationReward = (situationIndex, rewardIndex) => {
      if (situations.value[situationIndex].rewards.length > 1) {
        situations.value[situationIndex].rewards.splice(rewardIndex, 1);
      }
    };

    const addRequiredSkill = () => {
      requiredSkills.value.push({ name: '' });
    };

    const removeRequiredSkill = (index) => {
      if (requiredSkills.value.length > 1) {
        requiredSkills.value.splice(index, 1);
      }
    };

    const addRequiredItem = () => {
      requiredItems.value.push({ name: '' });
    };

    const removeRequiredItem = (index) => {
      if (requiredItems.value.length > 1) {
        requiredItems.value.splice(index, 1);
      }
    };

    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    const toggleMerchantEnabled = () => {
      isMerchantEnabled.value = !isMerchantEnabled.value;
    };

    const handleAddMap = async () => {
      if (totalGhostChance.value > 100) {
        alert('โอกาสการเจอวิญญาณรวมกันต้องไม่เกิน 100%');
        return;
      }

      if (totalBossGhostChance.value > 10000) {
        alert('โอกาสการเจอบอสวิญญาณรวมกันต้องไม่เกิน 10000 (100%)');
        return;
      }

      if (maxGhosts.value < minGhosts.value) {
        alert('จำนวนวิญญาณสูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      if (diamondDropMax.value < diamondDropMin.value) {
        alert('จำนวนเพชรสูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      if (hasInvalidSituations.value) {
        alert('จำนวนวิญญาณสูงสุดในสถานการณ์ต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      try {
        const filteredRewards = rewards.value.filter(r => r.itemName && r.dropChance > 0);
        const filteredGhosts = ghosts.value.filter(g => g.name && g.chance > 0);
        const filteredBossGhosts = bossGhosts.value.filter(bg => bg.name && bg.chance > 0);
        const filteredMerchants = isMerchantEnabled.value ? merchants.value.filter(m => m.name && m.chance > 0) : [];
        const filteredSituations = situations.value
          .filter(s => s.name && s.chance > 0)
          .map(s => ({
            name: s.name,
            minGhosts: s.minGhosts,
            maxGhosts: s.maxGhosts,
            chance: s.chance,
            rewards: s.rewards.filter(r => r.itemName && r.dropChance > 0),
          }));
        const filteredRequiredSkills = requiredSkills.value.filter(s => s.name).map(s => s.name);
        const filteredRequiredItems = requiredItems.value.filter(i => i.name).map(i => i.name);
        const mapData = {
          type: type.value,
          name: name.value,
          imageUrl: imageUrl.value,
          requiredLevel: requiredLevel.value,
          roomRequired: roomRequired.value,
          requiredEnergy: requiredEnergy.value,
          reduceHappy: reduceHappy.value,
          requiredCoins: requiredCoins.value,
          requiredGold: requiredGold.value,
          requiredDiamonds: requiredDiamonds.value,
          requiredCash: requiredCash.value,
          requiredRenown: requiredRenown.value,
          requiredMap: requiredMap.value || null,
          allowedBuildings: allowedBuildings.value.length > 0 ? allowedBuildings.value.map(Number) : [],
          escapeAttempts: escapeAttempts.value,
          escapeCoins: escapeCoins.value,
          isActive: isActive.value,
          isMerchantEnabled: isMerchantEnabled.value,
          minGhosts: minGhosts.value,
          maxGhosts: maxGhosts.value,
          ghosts: filteredGhosts.map(g => ({ name: g.name, chance: g.chance || 0 })),
          ghostNames: filteredGhosts.map(g => g.name),
          bossGhosts: filteredBossGhosts.map(bg => ({ name: bg.name, chance: bg.chance || 0 })),
          bossGhostNames: filteredBossGhosts.map(bg => bg.name),
          merchants: filteredMerchants.map(m => ({ name: m.name, chance: m.chance || 0 })),
          merchantNames: filteredMerchants.map(m => m.name),
          rewards: filteredRewards,
          rewardItemNames: filteredRewards.map(r => r.itemName),
          situations: filteredSituations,
          situationNames: filteredSituations.map(s => s.name),
          diamondDropMin: diamondDropMin.value,
          diamondDropMax: diamondDropMax.value,
          diamondDropChance: diamondDropChance.value,
          requiredSkills: filteredRequiredSkills,
          meetPlayerChance: meetPlayerChance.value,
          requiredItems: filteredRequiredItems,
          createdAt: new Date(),
        };

        await projectFirestore.collection('maps').doc(name.value).set(mapData);
        context.emit('mapAdded');

        // Reset form
        type.value = 'โลกวิญญาณ';
        name.value = '';
        imageUrl.value = '';
        requiredLevel.value = 1;
        roomRequired.value = 1;
        requiredEnergy.value = 0;
        reduceHappy.value = 1;
        requiredCoins.value = 0;
        requiredGold.value = 0;
        requiredDiamonds.value = 0;
        requiredCash.value = 0;
        requiredRenown.value = 0;
        requiredMap.value = '';
        allowedBuildings.value = [];
        escapeAttempts.value = 0;
        escapeCoins.value = 0;
        isActive.value = true;
        isMerchantEnabled.value = true;
        minGhosts.value = 5;
        maxGhosts.value = 10;
        ghosts.value = [{ name: '', chance: 0 }];
        bossGhosts.value = [{ name: '', chance: 0 }];
        merchants.value = [{ name: '', chance: 0 }];
        rewards.value = [{ itemName: '', dropChance: 0 }];
        situations.value = [{ name: '', minGhosts: 1, maxGhosts: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] }];
        diamondDropMin.value = 0;
        diamondDropMax.value = 0;
        diamondDropChance.value = 0;
        requiredSkills.value = [{ name: '' }];
        meetPlayerChance.value = 0;
        requiredItems.value = [{ name: '' }];
      } catch (error) {
        console.error('Error adding map:', error);
      }
    };

    return {
      cancelModal,
      type,
      name,
      imageUrl,
      requiredLevel,
      roomRequired,
      requiredEnergy,
      reduceHappy,
      requiredCoins,
      requiredGold,
      requiredDiamonds,
      requiredCash,
      requiredMap,
      allowedBuildings,
      requiredRenown,
      escapeAttempts,
      escapeCoins,
      isActive,
      isMerchantEnabled,
      minGhosts,
      maxGhosts,
      ghosts,
      bossGhosts,
      merchants,
      rewards,
      situations,
      diamondDropMin,
      diamondDropMax,
      diamondDropChance,
      requiredSkills,
      meetPlayerChance,
      requiredItems,
      totalGhostChance,
      remainingGhostChance,
      totalBossGhostChance,
      remainingBossGhostChance,
      hasInvalidSituations,
      normalizeGhostChances,
      addGhost,
      removeGhost,
      normalizeBossGhostChances,
      addBossGhost,
      removeBossGhost,
      addMerchant,
      removeMerchant,
      addReward,
      removeReward,
      addSituation,
      removeSituation,
      addSituationReward,
      removeSituationReward,
      addRequiredSkill,
      removeRequiredSkill,
      addRequiredItem,
      removeRequiredItem,
      toggleActive,
      toggleMerchantEnabled,
      handleAddMap,
      tabs,
      currentTab,
      switchTab
    };
  },
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #6366f1 #4b5563;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #4b5563;
  border-radius: 4px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 4px;
  border: 2px solid #4b5563;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #818cf8;
}
</style>